'use client';
import * as React from 'react';
import * as RS from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../cn';

export interface SelectOption {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}
export interface SelectProps {
  value?: string | null;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md';
  id?: string;
  name?: string;
  'aria-label'?: string;
}

export function Select({ value, onValueChange, options, placeholder = 'Select…', disabled, className, size = 'md', id, name, ...aria }: SelectProps) {
  return (
    <RS.Root value={value ?? undefined} onValueChange={onValueChange} disabled={disabled} name={name}>
      <RS.Trigger
        id={id}
        aria-label={aria['aria-label']}
        className={cn(
          'inline-flex w-full items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-fill-3 px-3 text-left text-[14px] text-text transition-colors hover:bg-fill-2 data-[placeholder]:text-text-3 disabled:opacity-50 focus:shadow-[0_0_0_3px_var(--accent-ring)] outline-none',
          size === 'sm' ? 'h-8 text-[13px]' : 'h-9',
          className,
        )}
      >
        <span className="truncate">
          <RS.Value placeholder={placeholder} />
        </span>
        <RS.Icon asChild>
          <ChevronDown className="size-4 text-text-3 shrink-0" />
        </RS.Icon>
      </RS.Trigger>
      <RS.Portal>
        <RS.Content position="popper" sideOffset={6} className="ui-rise z-[60] min-w-[var(--radix-select-trigger-width)] max-h-[320px] overflow-hidden rounded-[var(--radius)] bg-surface shadow-[var(--shadow-lg)] p-1">
          <RS.Viewport>
            {options.map((o) => (
              <RS.Item key={o.value} value={o.value} disabled={o.disabled} className="relative flex cursor-default select-none items-center rounded-[var(--radius-sm)] py-1.5 pl-8 pr-3 text-[14px] text-text outline-none data-[highlighted]:bg-accent data-[highlighted]:text-white data-[disabled]:opacity-40">
                <RS.ItemIndicator className="absolute left-2.5 inline-flex">
                  <Check className="size-4" />
                </RS.ItemIndicator>
                <div className="min-w-0">
                  <RS.ItemText>{o.label}</RS.ItemText>
                  {o.description ? <div className="text-[12px] opacity-70">{o.description}</div> : null}
                </div>
              </RS.Item>
            ))}
          </RS.Viewport>
        </RS.Content>
      </RS.Portal>
    </RS.Root>
  );
}

/** Native select styled like the Radix one; use inside plain forms / server-rendered pages. */
export const NativeSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(function NativeSelect({ className, children, ...props }, ref) {
  return (
    <div className="relative">
      <select ref={ref} className={cn('appearance-none w-full h-9 pl-3 pr-8 rounded-[var(--radius-sm)] bg-fill-3 text-[14px] text-text hover:bg-fill-2 focus:bg-surface focus:shadow-[0_0_0_3px_var(--accent-ring)] outline-none disabled:opacity-50', className)} {...props}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-text-3" />
    </div>
  );
});
